import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { api } from '../../../lib/api';
import { toAbsoluteApiMediaUrl } from '../../../lib/mediaUrl';

type ProfileCoordinator = { id: number; full_name: string };
type ProfileEnrolledClass = { id: number; name: string; coordinator?: ProfileCoordinator | null };
type ProfileSubject = { id: number; name: string; classroom_name?: string | null };
type ProfileChild = { id: number; full_name: string; classroom_name?: string | null };

type BackendUserProfile = {
  id: number;
  full_name: string;
  role: string;
  avatar_url?: string | null;
  school_id?: number | null;
  school_name?: string | null;
  school_class_name?: string | null;
  enrolled_classes?: ProfileEnrolledClass[] | null;
  children?: ProfileChild[] | null;
  subjects?: ProfileSubject[] | null;
  admin_classes?: ProfileEnrolledClass[] | null;
};

const roleLabel = (role: string) => {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'profesor':
      return 'Profesor';
    case 'elev':
      return 'Elev';
    case 'parinte':
      return 'Părinte';
    default:
      return role;
  }
};

export default function UserProfileModal({
  userId,
  open,
  onClose,
  title,
}: {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  title?: string;
}) {
  const [profile, setProfile] = useState<BackendUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numericUserId = useMemo(() => {
    const n = userId ? Number(userId) : NaN;
    return Number.isFinite(n) ? n : null;
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    if (!numericUserId) return;

    setIsLoading(true);
    setError(null);
    setProfile(null);

    api
      .get<BackendUserProfile>(`/api/users/profile/${numericUserId}`)
      .then((resp) => setProfile(resp.data))
      .catch((e) => {
        const msg = e?.response?.data?.detail || 'Nu am putut încărca profilul.';
        setError(String(msg));
      })
      .finally(() => setIsLoading(false));
  }, [numericUserId, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Profil utilizator'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl bg-card rounded-lg shadow-lg">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {profile?.avatar_url ? (
              <Image
                src={toAbsoluteApiMediaUrl(profile.avatar_url) || profile.avatar_url}
                alt="Avatar"
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                {(profile?.full_name?.[0] || '?').toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate">
                {profile?.full_name || 'Profil'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {profile?.role ? roleLabel(profile.role) : ''}
                {profile?.school_name ? ` • ${profile.school_name}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Închide"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-5">
          {isLoading && <div className="text-sm text-muted-foreground">Se încarcă…</div>}
          {error && <div className="text-sm text-destructive">{error}</div>}

          {!isLoading && !error && profile && (
            <div className="space-y-5">
              {profile.role === 'parinte' && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Copii</h3>
                  {profile.children && profile.children.length > 0 ? (
                    <div className="space-y-2">
                      {profile.children.map((c) => (
                        <div key={c.id} className="flex items-start justify-between gap-3">
                          <div className="text-sm text-foreground">{c.full_name}</div>
                          <div className="text-sm text-muted-foreground whitespace-nowrap">
                            {c.classroom_name || 'Fără clasă'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">Nu are copii asociați.</div>
                  )}
                </div>
              )}

              {profile.role === 'elev' && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Clasă</h3>
                  <div className="text-sm text-foreground">
                    {profile.school_class_name || 'Fără clasă'}
                  </div>

                  <h3 className="text-sm font-semibold text-foreground mt-4 mb-2">Coordonator</h3>
                  {profile.enrolled_classes && profile.enrolled_classes.length > 0 ? (
                    <div className="space-y-2">
                      {profile.enrolled_classes.map((c) => (
                        <div key={c.id} className="text-sm text-muted-foreground">
                          <span className="text-foreground">{c.name}</span>
                          {c.coordinator ? ` • ${c.coordinator.full_name}` : ' • (neatribuit)'}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Nu am găsit coordonator (nu există înscriere acceptată).
                    </div>
                  )}
                </div>
              )}

              {profile.role === 'profesor' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Materii predate</h3>
                    {profile.subjects && profile.subjects.length > 0 ? (
                      <div className="space-y-2">
                        {profile.subjects.map((s) => (
                          <div key={s.id} className="text-sm text-muted-foreground">
                            <span className="text-foreground">{s.name}</span>
                            {s.classroom_name ? ` • ${s.classroom_name}` : ''}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">Nu are materii asociate.</div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Clase în administrare</h3>
                    {profile.admin_classes && profile.admin_classes.length > 0 ? (
                      <div className="space-y-2">
                        {profile.admin_classes.map((c) => (
                          <div key={c.id} className="text-sm text-foreground">
                            {c.name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">Nu administrează clase.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Închide
          </Button>
        </div>
      </div>
    </div>
  );
}
